import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from "react";
import { Input, Button, Dropdown, DropdownToggle, DropdownMenu } from "@ht/components";
import { TInput } from "@ht/components/src/forms/form-elements/types";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { sendMessage, typingMessage, sendOfflineMessage, chatWSConnecting } from "../../../app/chat/chatSlice";
import SendIcon from "@ht/shared/fonts/deos-pis/send";
import LinkIcon from "@ht/shared/fonts/deos-pis/link";
import AddIcon from "@ht/shared/fonts/deos-pis/add";
import CameraIcon from "@ht/shared/fonts/deos-pis/camera";
import ImageIcon from "@ht/shared/fonts/deos-pis/image";
import FileIcon from "@ht/shared/fonts/deos-pis/file";
import { uid } from "@ht/shared/methods";
import { IChat } from "../type";
import Webcam from "react-webcam";
import clsx from "clsx";
import { useHttpRequest } from "../../../hooks/useHttpRequest";
import mime from 'mime';
import CircularProgress from "../../../components/circular-progress";
import { MessageBox } from "../../../components/message-box";
import { CameraModal } from "./CameraModal";
interface IProps {
    roomId: string;
}
interface IFile {
    fileName: string;
    originalFileName: string;
}
const MessageFooter: FC<IProps> = ({ roomId }) => {
    const webcamRef = useRef<Webcam>(null);
    const attachmentRef = useRef<HTMLInputElement>(null);
    const dispatch = useAppDispatch();
    const { connected, activeRoom, configuration } = useAppSelector((state) => state.chat);
    const showWeblinkButton = process.env.REACT_APP_SHOW_WEBLINK_BUTTON == "true";
    const [attachmentAccept, setAttachmentAccept] = useState("");
    const [attachmentType, setAttachmentType] = useState<"FILE" | "IMAGE" | "">("");
    const { user } = useAppSelector((state) => state.auth);
    const deviceId = user?.deviceId ?? "";
    const senderName = user?.username ?? "";
    const [text, setText] = useState("");
    const [showCamera, setShowCamera] = useState(false);
    const { sendRequest, error, loading, progress, setProgress } = useHttpRequest(process.env.REACT_APP_CHAT_SERVICE);
    const [showMessageBox, setShowMessageBox] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");
    useEffect(() => {
        if (error) {
            const msg = error.response?.data.message;
            setValidationMessage(msg);
            setShowMessageBox(true);
        }
    }, [error]);
    const isSipRoom = /._SIP$/g.test(activeRoom.account)
    const sendTextMessage = (textMessage: string) => {
        const newMessage = {
            deviceId,
            senderName,
            roomId,
            content: {
                type: "TEXT",
                message: textMessage,
                files: []
            },
            sessionId: activeRoom.sessionId,
        } as IChat;

        if (connected) {
            dispatch(sendMessage(newMessage));
        }
        else {
            dispatch(sendOfflineMessage(newMessage));
            setTimeout(() => dispatch(chatWSConnecting()), 1000);
        }
    }
    const sendInitialWebLink = (link: string) => {
        const newMessage = {
            deviceId,
            senderName,
            roomId,
            content: {
                type: "WEBLINK",
                message: link,
                templateName: "weblink_generation",
                param: [link]
            },
            sessionId: activeRoom.sessionId,
        } as IChat;
        if (connected) {
            dispatch(sendMessage(newMessage));
        }
        else {
            dispatch(sendOfflineMessage(newMessage));
            setTimeout(() => dispatch(chatWSConnecting()), 1000);
        }
    }
    const submitHandler = (e: { preventDefault: () => void }) => {
        e.preventDefault();
        if (text.trim() != "") {
            sendTextMessage(text);
        }
        setText("");
    };
    const closeMessageBoxHandler = () => {
        setShowMessageBox((prev) => !prev);
    };
    const textHandler = (e: ChangeEvent<TInput>) => {
        setText(e.target.value);
        if (user) dispatch(typingMessage({ sessionId: activeRoom.sessionId, userName: user.username }));
    };
    const uploadAttachment = async (file: string | Blob, fileName: string) => {
        const body = new FormData();
        body.append('file', file, fileName);
        const response = await sendRequest({
            method: 'post', url: `/UploadAttachment/${user?.username}`,
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            body
        });
        const data = response?.data[0];
        return data as IFile;
    };
    const sendChatAttachment = (file: IFile) => {
        const mimeType = mime.getType(file.fileName);
        let type = "FILE";
        if (mimeType?.includes('video'))
            type = "VIDEO";
        else if (mimeType?.includes('image'))
            type = "IMAGE";
        else if (mimeType?.includes('audio'))
            type = "AUDIO";

        const newMessage = {
            deviceId,
            senderName,
            roomId,
            content: {
                type,
                message: "",
                files: [file]
            },
            sessionId: activeRoom.sessionId
        } as IChat;
        console.log('chat connected', connected);
        if (connected) {
            dispatch(sendMessage(newMessage));
        }
        else {
            dispatch(sendOfflineMessage(newMessage));
            setTimeout(() => dispatch(chatWSConnecting()), 1000);
        }
    }
    const getUploadFileValidationMsg = (filename: string, filesize: number) => {
        let validationMsg;
        if (!/\.(jpe?g|png|mp4|mp3|xlsx?|csv|docx?|pptx?|pdf|zip)$/i.test(filename)) {
            validationMsg = "Only (jpeg| jpg| png) image, (mp4) video, (mp3) audio, or (xls(x)| csv| doc(x)| ppt(x)| zip| pdf) document are allowed for attachment";
        } else if (filesize / 1000000 > 15) {
            validationMsg = "Maximum attachment size is 15MB";
        }
        return validationMsg;
    }
    const attachmentChangeHandler = async (e: ChangeEvent<any>) => {
        const file = e.target.files?.[0];
        if (file) {
            const validationMsg = getUploadFileValidationMsg(file.name, file.size);
            if (validationMsg) {
                setValidationMessage(validationMsg);
                setShowMessageBox(true);
            } else {
                const data = await uploadAttachment(file, file.name);
                if (data) sendChatAttachment(data);
            }
        }
        e.target.value = "";
    }
    const attachmentClickHandler = (type: "FILE" | "IMAGE", accept: string) => {
        setAttachmentAccept(accept);
        setAttachmentType(type);
    }
    // open file picker after the accept filter is applied
    useEffect(() => {
        if (attachmentType) {
            attachmentRef.current?.click();
            setAttachmentType("");
        }
    }, [attachmentType]);
    const captureHandler = useCallback(
        async () => {
            const imageSrc = webcamRef?.current?.getScreenshot();
            console.log('enter captureHandler()');
            if (imageSrc) {
                console.log('imageSrc Ready');
                setShowCamera(false);
                const res = await fetch(imageSrc);
                const file = await res.blob();
                const fileName = `CAM-${uid(21)}.jpg`;
                const data = await uploadAttachment(file, fileName);
                if (data) sendChatAttachment(data);
            }
        },
        [webcamRef]
    );

    const linkClickHandler = async () => {
        try {
            const response = await sendRequest({
                method: 'post', url: `/GenerateWebLink`,
                body: {
                    userName: activeRoom.account,
                    agentUserName: user?.username,
                    sessionId: activeRoom.sessionId
                }
            });
            const link = response.data as object;
            sendInitialWebLink(`${link}`);
        } catch (err) {
            console.log("link error", err);
        }
    }
    const getFooterButton = () => {
        const menuItemClass = "flex flex-row w-full items-center gap-1.5 px-1.5 py-2 hover:bg-gray-100";
        const menuIconClass = "text-gray-400 w-4 h-4";
        return (<>
            {showWeblinkButton &&
                <div className="flex-none w-12">
                    <Button shape="square" type="button" iconButton
                        onClick={linkClickHandler} className="!w-full h-10 bg-gray-100 hover:bg-gray-200 border-y-0 border-r-0 hover:border-l-gray-100 border-l-gray-100 outline-none" >
                        <LinkIcon className="text-gray-400" />
                    </Button>
                </div>
            }
            {configuration.allowAttachment &&
                <div className="flex flex-none w-12">
                    <div className="bg-gray-100 flex">
                        <div className="border-l border-l-gray-200 my-1"></div>
                    </div>
                    <Dropdown className="bg-gray-100 h-10 grow flex justify-center items-center" direction="up" showIcon={false} shape>
                        <DropdownToggle variant="texted" iconButton size="lg" shape="rounded" customStyle="only-icon"
                            className="text-[26px] w-[32px] h-[32px] pl-1 relative content-start">
                            <AddIcon className="w-5 h-5 text-gray-400" />
                        </DropdownToggle>
                        <DropdownMenu className="rounded p-1.5 !w-[10rem] mt-[1.125rem] -mr-[1.2rem] border-t-0 right-0 left-auto">
                            <div className="dropdown-list rounded-t">
                                <button type="button" className={menuItemClass} onClick={() => setShowCamera(true)}>
                                    <CameraIcon className={menuIconClass} />
                                    Take from Camera
                                </button>
                            </div>
                            <div className="dropdown-list rounded-t">
                                <button type="button" className={menuItemClass} onClick={() => attachmentClickHandler("IMAGE", ".jpg, .png, .jpeg, .mp4")}>
                                    <ImageIcon className={menuIconClass} />
                                    Image
                                </button>
                            </div>
                            <div className="dropdown-list rounded-t">
                                <button type="button" className={menuItemClass} onClick={() => attachmentClickHandler("FILE", ".doc, .docx, .xls, .xlsx, .csv, .pdf, .ppt, .pptx, .zip, .mp3")}>
                                    <FileIcon className={menuIconClass} />
                                    File
                                </button>
                            </div>
                        </DropdownMenu>
                    </Dropdown>
                    <input id="attachment" name="attachment" type="file" className="hidden"
                        ref={attachmentRef} accept={attachmentAccept} onChange={attachmentChangeHandler} />
                </div>
            }
            <div className="flex flex-none w-12">
                <div className="bg-gray-100 flex">
                    <div className="border-l border-l-gray-200 my-1"></div>
                </div>
                <Button shape="square" type="submit" iconButton
                    className="!w-full h-10 bg-gray-100 hover:bg-gray-200 border-y-0 border-r-0 hover:border-l-gray-100 border-l-gray-100 rounded-r-md outline-none" >
                    <SendIcon className="text-gray-400" />
                </Button>
            </div>
        </>
        )
    }
    return (
        <>
            <CircularProgress loading={loading} progress={progress} setProgress={setProgress} />
            <MessageBox type="warning" visible={showMessageBox} closeHandler={closeMessageBoxHandler} title="Warning" message={validationMessage} />
            <div className="message-footer rounded-b bg-white px-5 h-[68px] flex items-center border-t border-gray-100">
                <form autoComplete="off"
                    className="flex items-center justify-between flex-auto"
                    onSubmit={submitHandler}>

                    <div className="grow ">
                        <Input
                            disabled={isSipRoom}
                            id="message"
                            name="message"
                            placeholder="Type something here..."
                            className={clsx("!text-md !text-gray-700 placeholder-gray-400 border-none focus:bg-gray-100 rounded-l-md bg-gray-100 outline-none")}
                            onChange={textHandler}
                            value={text}
                        />
                    </div>
                    {!isSipRoom && getFooterButton()}
                </form>
                <CameraModal ref={webcamRef} visible={showCamera} onClose={() => { setShowCamera(false) }} onCapture={captureHandler} />
            </div>
        </>
    );
};

export default MessageFooter;
